'use client';

import Link from 'next/link';
import Image from 'next/image';
import { useState } from 'react';
import { GoogleLogin } from '@react-oauth/google';
import type { ApiSuccessResponse, AuthResponseData } from '@/shared';
import { API, ENV } from '../../config/env';
import { persistAuth } from '../../utils/storage';
import { api, ApiError } from '@/lib/api';

interface ValidationErrors {
  email?: string;
  password?: string;
}

export default function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [validationErrors, setValidationErrors] = useState<ValidationErrors>({});
  const [touched, setTouched] = useState<{ email: boolean; password: boolean }>({
    email: false,
    password: false,
  });

  const validateEmail = (value: string): string | undefined => {
    if (!value) return 'Email is required';
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(value)) return 'Please enter a valid email address';
    return undefined;
  };

  const validatePassword = (value: string): string | undefined => {
    if (!value) return 'Password is required';
    if (value.length < 6) return 'Password must be at least 6 characters';
    return undefined;
  };

  const validateField = (field: string, value: string) => {
    const nextError = field === 'email' ? validateEmail(value) : validatePassword(value);
    setValidationErrors((prev) => ({ ...prev, [field]: nextError }));
  };

  const handleBlur = (field: string, value: string) => {
    setTouched((prev) => ({ ...prev, [field]: true }));
    validateField(field, value);
  };

  const handleChange = (field: string, value: string) => {
    if (field === 'email') setEmail(value);
    if (field === 'password') setPassword(value);
    if (error) setError('');
    if (touched[field as keyof typeof touched]) validateField(field, value);
  };

  const validateForm = (): boolean => {
    const emailError = validateEmail(email);
    const passwordError = validatePassword(password);
    setValidationErrors({ email: emailError, password: passwordError });
    setTouched({ email: true, password: true });
    return !emailError && !passwordError;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    if (!validateForm()) {
      setLoading(false);
      return;
    }

    try {
      const result = await api.post<ApiSuccessResponse<AuthResponseData>>(API.LOGIN, { email, password });
      persistAuth(result.data.token, result.data.refreshToken, result.data);
      window.location.href = '/dashboard';
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.message);
      } else {
        setError('Hit a snag. Give it another shot.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSuccess = async (credentialResponse: { credential?: string }) => {
    if (!credentialResponse.credential) return;
    setLoading(true);
    setError('');
    try {
      const result = await api.post<ApiSuccessResponse<AuthResponseData>>(API.GOOGLE_AUTH, {
        idToken: credentialResponse.credential,
      });
      persistAuth(result.data.token, result.data.refreshToken, result.data);
      console.log('[google-auth] response data:', result.data);
      window.location.href = result.data.usernameRequired ? '/onboarding' : '/dashboard';
    } catch (err) {
      if (err instanceof ApiError) setError(err.message);
      else setError('Google sign-in failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background px-4 py-12 sm:px-6">
      <div className="mx-auto w-full max-w-md">
        <div className="mb-7 text-center">
          <Link href="/" className="inline-flex items-center gap-3">
            <Image src="/logo-icon.svg" alt="DiecastShowcase logo" width={44} height={44} className="h-11 w-11" priority />
            <span className="text-2xl font-extrabold tracking-tight text-foreground">DiecastShowcase</span>
          </Link>
          <h1 className="mt-6 text-3xl font-bold text-foreground">Welcome back</h1>
          <p className="mt-2 text-sm text-muted-foreground">Sign in to continue your collection journey.</p>
        </div>

        <div className="card-shell p-6 sm:p-7">
          <form onSubmit={handleSubmit} className="space-y-5">
            {error && (
              <div className="rounded-lg border border-destructive/35 bg-destructive/10 px-4 py-3 text-sm text-destructive">
                {error}
              </div>
            )}

            <div>
              <label htmlFor="email" className="mb-2 block text-xs font-semibold uppercase tracking-[0.12em] text-muted-foreground">
                Email
              </label>
              <input
                type="email"
                id="email"
                value={email}
                onChange={(e) => handleChange('email', e.target.value)}
                onBlur={(e) => handleBlur('email', e.target.value)}
                required
                className={`form-input ${validationErrors.email && touched.email ? 'border-destructive focus:ring-destructive' : ''}`}
                placeholder="you@example.com"
              />
              {validationErrors.email && touched.email && <p className="mt-1 text-sm text-destructive">{validationErrors.email}</p>}
            </div>

            <div>
              <label htmlFor="password" className="mb-2 block text-xs font-semibold uppercase tracking-[0.12em] text-muted-foreground">
                Password
              </label>
              <input
                type="password"
                id="password"
                value={password}
                onChange={(e) => handleChange('password', e.target.value)}
                onBlur={(e) => handleBlur('password', e.target.value)}
                required
                className={`form-input ${validationErrors.password && touched.password ? 'border-destructive focus:ring-destructive' : ''}`}
                placeholder="Enter password"
              />
              {validationErrors.password && touched.password && <p className="mt-1 text-sm text-destructive">{validationErrors.password}</p>}
            </div>

            <div className="flex items-center justify-between">
              <label className="flex items-center gap-2 text-sm text-secondary-foreground">
                <input type="checkbox" className="h-4 w-4 rounded border-border bg-input" />
                Remember me
              </label>
              <Link href="/forgot-password" className="text-sm font-semibold text-primary">
                Forgot password?
              </Link>
            </div>

            <button type="submit" disabled={loading} className="btn-primary w-full justify-center">
              {loading ? 'Signing in...' : 'Sign in'}
            </button>
          </form>

          {ENV.GOOGLE_CLIENT_ID && (
            <div className="mt-6">
              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-border" />
                </div>
                <div className="relative flex justify-center text-xs uppercase tracking-[0.12em] text-muted-foreground">
                  <span className="bg-card px-2">Or continue with</span>
                </div>
              </div>
              <div className="mt-4 flex justify-center">
                <GoogleLogin onSuccess={handleGoogleSuccess} onError={() => setError('Google sign-in failed. Please try again.')} width="100%" />
              </div>
            </div>
          )}

          <p className="mt-6 text-center text-sm text-secondary-foreground">
            New here?{' '}
            <Link href="/signup" className="font-semibold text-primary">
              Create your account
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}

